import { FuelPricesMode, StoreFuelGradeFragment } from '@ocx/graphql';
import { IPrice, PriceGroup, PriceType, UnformattedPrice } from './types';
import { FuelGradesConfiguration } from '../configuration/app-config.types';
import { convertDollarsToCents } from '../../lib/formatters/formatPrice';

export interface IMapFuelGrades {
  grades: StoreFuelGradeFragment[];
  config: FuelGradesConfiguration;
  formatPrice(price: number): string;
  priceType?: PriceType;
}

export const createFuelGradeComparator = (config: FuelGradesConfiguration) => {
  return (a: UnformattedPrice, b: UnformattedPrice) => {
    const indexA = config.mapping.findIndex(([title]) => title === a.title);
    const indexB = config.mapping.findIndex(([title]) => title === b.title);

    if (indexA === -1) {
      return 1;
    }
    if (indexB === -1) {
      return -1;
    }

    return indexA - indexB;
  };
};

const createFormatPriceMapper = (formatter: (price: number) => string) => {
  return (item: UnformattedPrice): IPrice => ({
    ...item,
    price: formatter(item.price),
  });
};

const sortByAscPrice = (a: UnformattedPrice, b: UnformattedPrice) => {
  return a.price - b.price;
};

const createFuelGradeMapper = (config: FuelGradesConfiguration, priceType = PriceType.CREDIT) => {
  return (grade: IGradeWithUnitPrice): UnformattedPrice => {
    const gradeDescription = grade.description.toUpperCase();
    const mappedTitle = config.mapping.find(([name, descriptions]) =>
      descriptions.find((description) => gradeDescription === description.toUpperCase()),
    )?.[0];

    return {
      id: gradeDescription,
      title: mappedTitle || grade.description,
      price: convertDollarsToCents(grade.unitPrice[priceType] || 0),
      isDefault: mappedTitle === config.default,
      type: priceType,
    };
  };
};

type IGradeWithUnitPrice = Omit<StoreFuelGradeFragment, 'unitPrice'> & {
  unitPrice: NonNullable<StoreFuelGradeFragment['unitPrice']>;
};

const isGradeWithUnitPrice = (grade: StoreFuelGradeFragment): grade is IGradeWithUnitPrice => grade.unitPrice !== null;

export const mapFuelGrades = ({
  grades,
  config,
  formatPrice,
  priceType = PriceType.CREDIT,
}: IMapFuelGrades): IPrice[] => {
  const mapper = createFuelGradeMapper(config, priceType);
  const formatPriceMapper = createFormatPriceMapper(formatPrice);
  const sortCompare = createFuelGradeComparator(config);
  const isAscending = config.sortPrices === 'Asc';

  return grades
    .filter(isGradeWithUnitPrice)
    .map(mapper)
    .filter((grade) => grade.price !== 0) // remove zero prices (0.00, 0, 0.0, etc...)
    .sort(isAscending ? sortByAscPrice : sortCompare)
    .map(formatPriceMapper);
};

const serializeGradeGroupPrices = (group: PriceGroup) => group.prices.map((grade) => grade.price).join(',');
const isNotEmptyGradeGroup = (gradeGroup: PriceGroup) => !!gradeGroup.prices.length;
const isNotDuplicatedGradeGroup = (gradeGroup: PriceGroup, index: number, array: PriceGroup[]) => {
  if (index === 0) {
    return true;
  }
  const ser = serializeGradeGroupPrices(gradeGroup);
  return !array.slice(0, index).find((group) => serializeGradeGroupPrices(group) === ser);
};

type MapFuelGradeGroupsProps = Omit<IMapFuelGrades, 'priceType'> & {
  fuelPricesMode: FuelPricesMode;
  priceTypeTitles: Record<PriceType, string>;
};

export const mapFuelGradeGroups = ({
  grades,
  config,
  formatPrice,
  fuelPricesMode,
  priceTypeTitles,
}: MapFuelGradeGroupsProps): PriceGroup[] => {
  let groups;

  if (fuelPricesMode === FuelPricesMode.Both) {
    groups = [
      {
        title: priceTypeTitles[PriceType.CREDIT],
        prices: mapFuelGrades({ grades, config, formatPrice, priceType: PriceType.CREDIT }),
      },
      {
        title: priceTypeTitles[PriceType.CASH],
        prices: mapFuelGrades({ grades, config, formatPrice, priceType: PriceType.CASH }),
      },
    ];
  } else if (fuelPricesMode === FuelPricesMode.CashOnly) {
    groups = [
      {
        title: priceTypeTitles[PriceType.CASH],
        prices: mapFuelGrades({ grades, config, formatPrice, priceType: PriceType.CASH }),
      },
    ];
  } else {
    groups = [
      {
        title: priceTypeTitles[PriceType.CREDIT],
        prices: mapFuelGrades({ grades, config, formatPrice, priceType: PriceType.CREDIT }),
      },
    ];
  }

  // sort FuelGradesGroups by price descending, remove empty groups and remove groups with the same list of prices

  const getFuelNumValue = (group: PriceGroup) => {
    return parseFloat(
      (group.prices.find((price) => price.isDefault)?.price || group.prices[0].price).replace(/[^\d]/, ''),
    );
  };

  groups = groups
    .filter(isNotEmptyGradeGroup)
    .filter(isNotDuplicatedGradeGroup)
    .sort((a, b) => {
      return getFuelNumValue(a) - getFuelNumValue(b);
    });

  if (groups.length > 0) {
    groups[0].title = groups[0].title.replace(/Prices/, 'Discount Prices');
  }

  return groups;
};
